import { showErrorMessage } from 'components/Helpers/Toasts';
import { getUserFriendlyErrors } from 'connectors/lib/friendlyErrorMessageParser';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { CartFragmentApi, useChangePaymentInCartMutationApi } from 'graphql/generated';
import { useLatest } from 'hooks/ui/useLatest';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { GtmCartInfoEventType, GtmMessageOriginType } from 'types/gtm';
import { onPaymentChangeGtmEventHandler } from 'utils/Gtm/EventHandlers';
import { useGtmCartEventInfo } from 'utils/Gtm/Gtm';
import { shallow } from 'zustand/shallow';

export type ChangePaymentHandler = (
    newPaymentUuid: string | null,
    newGoPayBankSwift: string | null,
) => Promise<CartFragmentApi | undefined | null>;

export const useChangePaymentMutation = (): [ChangePaymentHandler, boolean] => {
    return useChangePaymentMutationWithGtm(useGtmCartEventInfo());
};

export const useChangePaymentMutationWithGtm = (
    gtmCartEventInfo: GtmCartInfoEventType,
): [ChangePaymentHandler, boolean] => {
    const [{ fetching }, changePaymentInCart] = useChangePaymentInCartMutationApi();
    const { cartUuid, defaultStoreUuid } = usePersistStore(
        (s) => ({ cartUuid: s.cartUuid, defaultStoreUuid: s.defaultStoreUuid }),
        shallow,
    );
    const { currencyCode } = useDomainConfig();
    const { t } = useTranslation();
    const gtmCart = useLatest(gtmCartEventInfo.cart);

    const changePaymentHandler = useCallback<ChangePaymentHandler>(
        async (newPaymentUuid, newGoPayBankSwift) => {
            const changePaymentResult = await changePaymentInCart(
                {
                    input: { paymentUuid: newPaymentUuid, paymentGoPayBankSwift: newGoPayBankSwift, cartUuid },
                    defaultStoreUuid,
                    selectedStoreUuid: null,
                },
                { additionalTypenames: ['dedup'] },
            );

            // EXTEND PAYMENT MODIFICATIONS HERE

            if (changePaymentResult.error !== undefined) {
                const { userError } = getUserFriendlyErrors(changePaymentResult.error, t);
                if (userError?.validation?.payment !== undefined) {
                    showErrorMessage(userError.validation.payment.message, GtmMessageOriginType.transport_and_payment);
                }
                if (userError?.validation?.goPaySwift !== undefined) {
                    showErrorMessage(
                        userError.validation.goPaySwift.message,
                        GtmMessageOriginType.transport_and_payment,
                    );
                }

                return null;
            }

            onPaymentChangeGtmEventHandler(
                gtmCart.current,
                changePaymentResult.data?.ChangePaymentInCart.payment ?? null,
                currencyCode,
            );

            return changePaymentResult.data?.ChangePaymentInCart;
        },
        [cartUuid, changePaymentInCart, currencyCode, defaultStoreUuid, gtmCart, t],
    );

    return [changePaymentHandler, fetching];
};
